/**
 * Slack authorization for data access — a hand-rolled popup + `postMessage`
 * flow, since Slack has no Google-Identity-Services-like SDK to lean on.
 * Mirrors `google.ts`'s `requestGmailAuthCode` shape/contract so the
 * "connect a workspace" wiring in `StudioBoard.tsx` looks the same for both.
 *
 * Each call authorizes exactly one Slack workspace (Slack OAuth is scoped
 * per-workspace) — connecting several workspaces means calling this, and the
 * `connectSlackWorkspace` callable, once per workspace.
 */

// Read-only user token scopes — kept in sync with `SLACK_USER_SCOPES` in
// `functions/src/slack.ts` (documented there, not re-imported since this is
// a separate frontend bundle).
const SLACK_USER_SCOPES =
  'search:read,channels:history,channels:read,groups:history,groups:read,' +
  'im:history,im:read,mpim:history,mpim:read,users:read';

interface SlackCallbackMessage {
  source: 'mithril-slack-callback';
  code?: string;
  state?: string;
  error?: string;
}

function randomState(): string {
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

/**
 * Open a popup for the user to authorize one Slack workspace, and resolve
 * with the one-time authorization code once `/slack/callback` posts it back
 * via `postMessage`. Rejects if the popup is blocked, closed early, or Slack
 * reports an error.
 */
export async function requestSlackAuthCode(): Promise<{ code: string; redirectUri: string }> {
  const clientId = (import.meta.env as Record<string, string | undefined>).VITE_SLACK_CLIENT_ID;
  if (!clientId) {
    throw new Error('Slack is not configured (missing VITE_SLACK_CLIENT_ID).');
  }

  const redirectUri = `${window.location.origin}/slack/callback`;
  const state = randomState();
  const authorizeUrl =
    'https://slack.com/oauth/v2/authorize' +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&user_scope=${encodeURIComponent(SLACK_USER_SCOPES)}` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&state=${encodeURIComponent(state)}`;

  const popup = window.open(authorizeUrl, 'slack-connect', 'width=520,height=700');
  if (!popup) {
    throw new Error('Popup blocked — allow popups for this site and try again.');
  }

  const code = await new Promise<string>((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.clearInterval(closedCheck);
    };
    const onMessage = (event: MessageEvent<SlackCallbackMessage>) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.source !== 'mithril-slack-callback') return;
      settled = true;
      cleanup();
      if (data.state !== state) {
        reject(new Error('Slack authorization state mismatch — try again.'));
      } else if (data.code) {
        resolve(data.code);
      } else {
        reject(new Error(data.error || 'Slack authorization cancelled.'));
      }
    };
    window.addEventListener('message', onMessage);
    // Popups closed by the user (rather than by the callback page) never
    // post a message — poll for that so the promise doesn't hang forever.
    const closedCheck = window.setInterval(() => {
      if (popup.closed && !settled) {
        cleanup();
        reject(new Error('Slack authorization window closed.'));
      }
    }, 500);
  });

  return { code, redirectUri };
}
